/**
 * Who is signed in, and on which platform.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '@/api/client'
import type { Tokens } from '@/api/types'
import { reachGoal, startMetrika } from '@/lib/metrika'
import { detectPlatform } from '@/platform/detect'
import type { Platform } from '@/platform/types'

interface AuthState {
  ready: boolean
  platform: Platform | null
  tokens: Tokens | null
  isAuthenticated: boolean
  signIn(tokens: Tokens): void
  signOut(): Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [platform, setPlatform] = useState<Platform | null>(null)
  const [tokens, setTokens] = useState<Tokens | null>(() => api.tokens)

  const signIn = useCallback((fresh: Tokens) => {
    api.setTokens(fresh)
    setTokens(fresh)
    if (fresh.is_new) reachGoal('signup')
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // The session is gone locally either way
    }
    api.setTokens(null)
    setTokens(null)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function start() {
      const detected = await detectPlatform()
      await detected.init()
      if (detected.kind === 'browser') startMetrika()
      api.setClient(detected.kind === 'telegram' ? 'miniapp' : 'pwa')
      api.setLogoutHandler(() => setTokens(null))

      try {
        const result = await detected.authenticate(api)
        if (!cancelled && result) {
          api.setTokens(result)
          setTokens(result)
        }
      } catch {
        // Telegram sign-in failed: the Login screen will offer email
      }

      if (!cancelled) {
        setPlatform(detected)
        setReady(true)
      }
    }

    void start()
    return () => {
      cancelled = true
      api.setLogoutHandler(null)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      ready,
      platform,
      tokens,
      isAuthenticated: Boolean(tokens),
      signIn,
      signOut,
    }),
    [ready, platform, tokens, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}

export function usePlatform(): Platform | null {
  return useAuth().platform
}
